<template>
  <div class="container mx-auto p-4">
    <Banner />
    <!-- Loading Overlay -->
    <div
      v-if="isLoading"
      class="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div class="text-center">
        <div class="relative">
          <!-- Animated Spinner -->
          <div
            class="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"
          ></div>
          <!-- Pulsing Dots -->
          <div class="flex justify-center space-x-1 mb-4">
            <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            <div
              class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
              style="animation-delay: 0.1s"
            ></div>
            <div
              class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
              style="animation-delay: 0.2s"
            ></div>
          </div>
        </div>
        <h3 class="text-base sm:text-lg font-semibold text-gray-700 mb-2">
          {{ loadingMessage }}
        </h3>
        <p class="text-xs sm:text-sm text-gray-500">
          Please wait while we load your data...
        </p>
      </div>
    </div>
    <div class="min-h-screen px-2 sm:p-4 lg:p-6">
      <div class="max-w-7xl mx-auto">
        <!-- Header Cards - Mobile Responsive Grid -->
        <div class="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
          <AttendanceCard
            viewKey="attendance"
            title="Give Attendance"
            description="Mark daily attendance for students"
            :iconPath="'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'"
            :isActive="currentView === 'attendance'"
            color="blue"
            @select="currentView = $event"
            :class="
              currentView === 'attendance'
                ? 'ring-1 sm:ring-2 ring-blue-500 bg-blue-50/70'
                : ''
            "
          />
          <AttendanceCard
            viewKey="details"
            title="Attendance Details"
            description="View attendance reports and analytics"
            :iconPath="'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'"
            :isActive="currentView === 'details'"
            color="blue"
            @select="currentView = $event"
            :class="
              currentView === 'details'
                ? 'ring-1 sm:ring-2 ring-blue-500 bg-blue-50/70'
                : ''
            "
          />
        </div>
        <div v-if="currentView === 'attendance'">
          <!-- Filter Section - Mobile First -->
          <div
            class="bg-white rounded-lg sm:rounded-1xl p-4 sm:p-6 border border-gray-200 mb-2 lg:mb-6"
          >
            <!-- Mobile Filter Header -->
            <div class="flex items-center justify-between mb-4 sm:hidden">
              <h3 class="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                @click="showMobileFilters = !showMobileFilters"
                class="p-2 text-gray-600"
              >
                <svg
                  v-if="!showMobileFilters"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  ></path>
                </svg>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
            <!-- Filter Content -->
            <div
              :class="[
                'sm:block transition-all duration-300',
                showMobileFilters ? 'block' : 'hidden sm:block',
              ]"
            >
              <div
                class="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 sm:items-end sm:justify-between"
              >
                <!-- Left side: Search and Class Filter -->
                <div class="flex flex-col sm:flex-row gap-3 md:gap-4 flex-1">
                  <!-- Search Input -->
                  <div class="flex-1 sm:max-w-sm lg:max-w-md">
                    <label
                      class="block text-sm font-medium text-gray-700 mb-2 sm:hidden"
                      >Search Student</label
                    >
                    <div class="relative group">
                      <Search
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4 group-focus-within:text-blue-600 transition-colors"
                      />
                      <input
                        v-model="search"
                        type="text"
                        placeholder="Search student by name..."
                        class="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none transition-all duration-200 bg-white text-gray-800 shadow-sm hover:border-gray-400 text-sm sm:text-base"
                      />
                      <!-- Search Loading Indicator -->
                      <div
                        v-if="isSearching"
                        class="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <div
                          class="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"
                        ></div>
                      </div>
                    </div>
                  </div>

                  <!-- Class Dropdown -->
                  <div class="sm:w-56 lg:w-64">
                    <label
                      class="block text-sm font-medium text-gray-700 mb-2 sm:hidden"
                      >Class</label
                    >
                    <div class="relative group">
                      <GraduationCap
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4 group-focus-within:text-blue-600 transition-colors"
                      />
                      <select
                        v-model="selectedClass"
                        :disabled="isLoading"
                        class="w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none transition-all duration-200 bg-white text-gray-800 appearance-none disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base shadow-sm hover:border-gray-400"
                      >
                        <option value="">All Classes</option>
                        <option
                          v-for="cls in availableClasses.filter(
                            (c) => c.name.toLowerCase() !== 'passout',
                          )"
                          :key="cls._id"
                          :value="cls._id"
                          :title="`${cls.name}${
                            cls.section ? ` - Section ${cls.section}` : ''
                          }`"
                        >
                          {{ cls.name
                          }}{{ cls.section ? ` - ${cls.section}` : "" }}
                        </option>
                      </select>
                      <ChevronDown
                        class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4 pointer-events-none"
                      />
                    </div>
                  </div>
                  <!-- Date Filter -->
                  <div class="sm:w-56 lg:w-64">
                    <label
                      class="block text-sm font-medium text-gray-700 sm:hidden"
                    >
                      Date
                    </label>
                    <input
                      v-model="selectedDate"
                      type="date"
                      :max="getCurrentDate()"
                      :disabled="isLoading"
                      class="w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none transition-all duration-200 bg-white text-gray-800 appearance-none disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base shadow-sm hover:border-gray-400"
                    />
                  </div>
                </div>

                <!-- Right side: Action Buttons -->
                <div class="flex space-x-2 sm:space-x-3 flex-shrink-0">
                  <button
                    @click="fetchFilteredStudents"
                    :disabled="isLoading"
                    class="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 sm:px-6 py-3 sm:py-2.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base min-h-[44px]"
                  >
                    <div
                      v-if="isLoading"
                      class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                    ></div>
                    <span>{{ isLoading ? "Loading..." : "Submit" }}</span>
                  </button>

                  <button
                    @click="refreshData"
                    :disabled="isLoading"
                    class="p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] border border-gray-200"
                  >
                    <RefreshCw
                      :class="['w-4 h-4', isLoading ? 'animate-spin' : '']"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- Content Area with Loading State -->
          <div class="relative">
            <!-- Content Loading Overlay -->
            <div
              v-if="isContentLoading"
              class="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center"
            >
              <div class="text-center">
                <div
                  class="w-6 h-6 sm:w-8 sm:h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"
                ></div>
                <p class="text-xs sm:text-sm text-gray-600">
                  Loading students...
                </p>
              </div>
            </div>
            <div
              v-if="students.length > 0"
              class="bg-white rounded-xl border border-gray-200"
            >
              <div class="p-4 sm:p-6 border-b border-gray-200">
                <div
                  class="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-4"
                >
                  <h2 class="text-lg sm:text-xl font-semibold text-gray-900">
                    {{ getClassName(selectedClass) }} - Student Attendance
                  </h2>
                  <!-- Stats - Mobile Responsive -->
                  <div class="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm">
                    <span class="flex items-center">
                      <div
                        class="w-3 h-3 bg-emerald-500 rounded-full mr-2"
                      ></div>
                      <span class="font-medium text-gray-700"
                        >Present: {{ getPresentCount() }}</span
                      >
                    </span>
                    <span class="flex items-center">
                      <div class="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      <span class="font-medium text-gray-700"
                        >Absent: {{ getAbsentCount() }}</span
                      >
                    </span>
                    <span class="flex items-center">
                      <div class="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                      <span class="font-medium text-gray-700"
                        >Late: {{ getLateCount() }}</span
                      >
                    </span>
                  </div>
                </div>
                <!-- Bulk Actions - Mobile Responsive -->
                <div class="flex flex-wrap gap-2 sm:gap-3">
                  <button
                    @click="markAllPresent"
                    :disabled="isLoading || !selectedClass"
                    class="px-3 sm:px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-[9px] sm:text-sm font-medium border border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
                  >
                    Mark All Present
                  </button>
                  <button
                    @click="markAllAbsent"
                    :disabled="isLoading || !selectedClass"
                    class="px-3 sm:px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-[9px] sm:text-sm font-medium border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
                  >
                    Mark All Absent
                  </button>
                </div>
              </div>
              <div class="p-4 sm:p-6">
                <!-- Mobile View - Cards (< 768px) -->
                <div class="block md:hidden space-y-4">
                  <div
                    v-for="student in students"
                    :key="student._id"
                    :class="[
                      'border-2 rounded-xl p-4 ',
                      attendance[student._id] === 'present'
                        ? 'border-green-200 bg-green-50'
                        : attendance[student._id] === 'absent'
                        ? 'border-red-200 bg-red-50'
                        : attendance[student._id] === 'late'
                        ? 'border-yellow-200 bg-yellow-50'
                        : 'border-gray-200 bg-white',
                    ]"
                  >
                    <!-- Student Info Header -->
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center space-x-3">
                        <div class="flex items-center gap-2">
                          <IdCard class="w-4 h-4 text-blue-600" />
                          <span class="text-sm font-medium text-gray-900">{{
                            student.rollNumber
                          }}</span>
                        </div>
                        <div
                          class="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center border border-blue-200 bg-blue-100"
                        >
                          <!-- If photo exists -->
                          <img
                            v-if="student.photo"
                            :src="student.photo"
                            alt="Student Photo"
                            class="w-full h-full rounded-full object-cover"
                          />
                          <!-- Fallback to first initial -->
                          <span
                            v-else
                            class="text-sm font-semibold text-blue-600"
                          >
                            {{ student.name.charAt(0).toUpperCase() }}
                          </span>
                        </div>

                        <div class="flex-1 min-w-0">
                          <span
                            class="text-[12px] md:text-sm font-medium text-gray-900 truncate block"
                          >
                            {{ student.name }}
                          </span>
                        </div>
                      </div>
                      <!-- 3-dot menu button -->
                      <div class="relative">
                        <button
                          @click="toggleDropdown(student._id)"
                          class="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                          <svg
                            class="w-4 h-4 text-gray-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                            />
                          </svg>
                        </button>
                        <!-- Dropdown menu -->
                        <div
                          v-if="openDropdown === student._id"
                          class="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[1000]"
                        >
                          <div class="p-2 space-y-2">
                            <!-- Late button -->
                            <button
                              @click="
                                setAttendance(student._id, 'late');
                                closeDropdown();
                              "
                              :disabled="isLoading || savingStates[student._id]"
                              :class="[
                                'w-full flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                                attendance[student._id] === 'late'
                                  ? 'bg-amber-500 text-white shadow-md'
                                  : alreadyMarked[student._id]
                                  ? 'bg-amber-50 text-amber-700 border border-amber-300'
                                  : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700 border border-gray-200',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                              ]"
                            >
                              <svg
                                class="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                ></path>
                              </svg>
                              Mark as Late
                            </button>

                            <!-- Undo Attendance -->
                            <button
                              @click="
                                undoAttendance(student._id);
                                closeDropdown();
                              "
                              :disabled="
                                isLoading ||
                                savingStates[student._id] ||
                                !alreadyMarked[student._id]
                              "
                              class="w-full flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 bg-red-50 text-red-700 border border-red-300 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <svg
                                class="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                ></path>
                              </svg>
                              Undo Attendance
                            </button>

                            <!-- Remarks input -->
                            <input
                              type="text"
                              v-model="remarks[student._id]"
                              placeholder="Add remarks..."
                              :disabled="isLoading || savingStates[student._id]"
                              class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                              @click.stop
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- MARKED BADGE BELOW HEADER -->
                    <div
                      v-if="alreadyMarked[student._id]"
                      class="mb-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full w-max"
                    >
                      Marked
                    </div>

                    <!-- Attendance Buttons - Mobile Grid -->
                    <div class="grid grid-cols-2 gap-2 mb-4">
                      <!-- Absent Button -->
                      <button
                        @click="setAttendance(student._id, 'absent')"
                        :disabled="isLoading || savingStates[student._id]"
                        :class="[
                          'flex items-center justify-center px-2 py-3 rounded-lg text-xs font-medium',
                          attendance[student._id] === 'absent'
                            ? 'bg-red-500 text-white shadow-md'
                            : attendance[student._id] === 'present'
                            ? 'bg-gray-200 text-gray-400'
                            : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-700 border border-gray-200',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                        ]"
                      >
                        <svg
                          class="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                        Absent
                      </button>

                      <!-- Present Button -->
                      <button
                        @click="setAttendance(student._id, 'present')"
                        :disabled="isLoading || savingStates[student._id]"
                        :class="[
                          'flex items-center justify-center px-2 py-3 rounded-lg text-xs font-medium',
                          attendance[student._id] === 'present'
                            ? 'bg-emerald-500 text-white shadow-md'
                            : attendance[student._id] === 'absent'
                            ? 'bg-gray-200 text-gray-400'
                            : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                        ]"
                      >
                        <svg
                          class="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                        Present
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Desktop View - Table (≥ 768px) -->
                <div class="hidden md:block overflow-x-auto">
                  <table class="w-full">
                    <thead>
                      <tr class="border-b border-gray-200">
                        <th
                          class="text-left py-4 px-4 font-semibold text-gray-700 text-sm"
                        >
                          Roll No.
                        </th>
                        <th
                          class="text-left py-4 px-4 font-semibold text-gray-700 text-sm"
                        >
                          Student Name
                        </th>
                        <th
                          class="text-center py-4 px-4 font-semibold text-gray-700 text-sm"
                        >
                          Attendance Status
                        </th>
                        <th
                          class="text-center py-4 px-4 font-semibold text-gray-700 text-sm"
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="student in students"
                        :key="student._id"
                        :class="[
                          'border-b transition-colors',
                          attendance[student._id] === 'present'
                            ? 'bg-green-50 hover:bg-green-100'
                            : attendance[student._id] === 'absent'
                            ? 'bg-red-50 hover:bg-red-100'
                            : attendance[student._id] === 'late'
                            ? 'bg-yellow-50 hover:bg-yellow-100'
                            : 'hover:bg-gray-50',
                        ]"
                      >
                        <!-- Roll No -->
                        <td
                          class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                          <div class="flex items-center gap-2">
                            <IdCard
                              class="w-6 h-6 lg:w-8 lg:h-8 text-blue-600"
                            />
                            <span>{{ student.rollNumber }}</span>
                          </div>
                        </td>

                        <!-- Student Name -->
                        <td class="py-4 px-4">
                          <div class="flex items-center">
                            <div
                              class="w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center mr-3 border border-blue-200 bg-blue-100"
                            >
                              <!-- If photo exists -->
                              <img
                                v-if="student.photo"
                                :src="student.photo"
                                alt="Student Photo"
                                class="w-full h-full rounded-full object-cover"
                              />
                              <!-- Fallback to first initial -->
                              <span
                                v-else
                                class="text-xs lg:text-sm font-semibold text-blue-600"
                              >
                                {{ student.name.charAt(0).toUpperCase() }}
                              </span>
                            </div>

                            <span
                              class="font-medium text-gray-900 text-sm lg:text-base"
                            >
                              {{ student.name }}
                              <span
                                v-if="alreadyMarked[student._id]"
                                class="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full"
                              >
                                (Marked)
                              </span>
                            </span>
                          </div>
                        </td>

                        <!-- Attendance Buttons -->
                        <td class="py-4 px-4">
                          <div
                            class="flex justify-center items-center space-x-1 lg:space-x-2"
                          >
                            <!-- Absent -->
                            <button
                              @click="setAttendance(student._id, 'absent')"
                              :disabled="isLoading || savingStates[student._id]"
                              :class="[
                                'px-2 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-medium transition-all duration-200 min-h-[36px] relative',
                                attendance[student._id] === 'absent'
                                  ? 'bg-red-500 text-white shadow-md'
                                  : attendance[student._id] === 'present'
                                  ? 'bg-gray-200 text-gray-400'
                                  : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-700 border border-gray-200',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                              ]"
                            >
                              <span>Absent</span>
                            </button>

                            <!-- Present -->
                            <button
                              @click="setAttendance(student._id, 'present')"
                              :disabled="isLoading || savingStates[student._id]"
                              :class="[
                                'px-2 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-medium transition-all duration-200 min-h-[36px] relative',
                                attendance[student._id] === 'present'
                                  ? 'bg-emerald-500 text-white shadow-md'
                                  : attendance[student._id] === 'absent'
                                  ? 'bg-gray-200 text-gray-400'
                                  : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                              ]"
                            >
                              <span>Present</span>
                            </button>
                          </div>
                        </td>

                        <!-- Action column (3-dot menu) -->
                        <td class="py-4 px-4 text-center">
                          <div class="relative inline-block">
                            <button
                              @click="toggleDropdown(student._id)"
                              class="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                              <svg
                                class="w-4 h-4 text-gray-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                                />
                              </svg>
                            </button>

                            <!-- Dropdown -->
                            <div
                              v-if="openDropdown === student._id"
                              class="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                            >
                              <div class="p-2 space-y-2">
                                <!-- Late -->
                                <button
                                  @click="
                                    setAttendance(student._id, 'late');
                                    closeDropdown();
                                  "
                                  :disabled="
                                    isLoading || savingStates[student._id]
                                  "
                                  :class="[
                                    'w-full flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                                    attendance[student._id] === 'late'
                                      ? 'bg-yellow-500 text-white shadow-md'
                                      : ['absent', 'present'].includes(
                                          attendance[student._id],
                                        )
                                      ? 'bg-gray-200 text-gray-400'
                                      : 'bg-gray-100 text-gray-600 hover:bg-yellow-50 hover:text-yellow-700 border border-gray-200',
                                    'disabled:opacity-50 disabled:cursor-not-allowed',
                                  ]"
                                >
                                  Late
                                </button>

                                <!-- Undo -->
                                <button
                                  @click="
                                    undoAttendance(student._id);
                                    closeDropdown();
                                  "
                                  :disabled="
                                    isLoading ||
                                    savingStates[student._id] ||
                                    !alreadyMarked[student._id]
                                  "
                                  class="w-full flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 bg-red-50 text-red-700 border border-red-300 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <svg
                                    class="w-4 h-4 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                      stroke-width="2"
                                      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                    ></path>
                                  </svg>
                                  Undo Attendance
                                </button>

                                <!-- Remarks -->
                                <input
                                  type="text"
                                  v-model="remarks[student._id]"
                                  placeholder="Add remarks..."
                                  :disabled="
                                    isLoading || savingStates[student._id]
                                  "
                                  class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                  @click.stop
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Save Button - Mobile Responsive -->
                <!-- Removed the save attendance button section completely -->
              </div>
            </div>
            <div v-else-if="!isLoading && !isContentLoading">
              <EmptyStateCard />
            </div>
          </div>
          <!-- Pagination Section - Mobile Responsive -->
          <div
            v-if="students.length"
            class="flex flex-col sm:flex-row items-center justify-between mt-6 sm:mt-8 space-y-4 sm:space-y-0 px-2"
          >
            <!-- Results summary -->
            <div
              class="text-xs sm:text-sm text-gray-600 font-medium text-center sm:text-left"
            >
              <template v-if="!isLoading">
                Showing
                <span class="font-bold text-gray-900">
                  {{ (currentPage - 1) * limit + 1 }}
                </span>
                to
                <span class="font-bold text-gray-900">
                  {{ Math.min(currentPage * limit, totalResults) }}
                </span>
                of
                <span class="font-bold text-gray-900">
                  {{ totalResults }}
                </span>
                results
              </template>
              <template v-else>
                <div
                  class="flex items-center gap-2 justify-center sm:justify-start"
                >
                  <div
                    class="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
                  ></div>
                  <span>Loading results...</span>
                </div>
              </template>
            </div>
            <!-- Pagination buttons -->
            <div class="flex items-center gap-1">
              <!-- Previous button -->
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1 || isLoading"
                class="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px] min-w-[36px]"
              >
                <ChevronLeft class="w-4 h-4" />
              </button>
              <!-- Numbered buttons -->
              <template
                v-for="page in totalPages"
                :key="page"
              >
                <button
                  @click="goToPage(page)"
                  :disabled="isLoading"
                  :class="[
                    'px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-xl transition-all duration-200 min-h-[36px] min-w-[36px]',
                    page === currentPage
                      ? 'font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md hover:shadow-lg'
                      : 'font-semibold text-gray-600 hover:text-blue-600 hover:bg-blue-50',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  ]"
                >
                  {{ page }}
                </button>
              </template>
              <!-- Next button -->
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage === totalPages || isLoading"
                class="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px] min-w-[36px]"
              >
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <div v-else-if="currentView === 'details'">
          <AttendanceDetails />
        </div>
      </div>
      <!-- Success Toast - Mobile Responsive -->
      <div
        v-if="showSuccessToast"
        class="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 sm:px-6 lg:px-8 py-4 sm:py-6 rounded-2xl shadow-2xl transition-all duration-500 transform animate-jump-in z-50 max-w-sm mx-auto sm:mx-0"
      >
        <ShowSuccess />
      </div>
    </div>
  </div>
</template>
<script setup>
  import { ref, onMounted, computed, watch, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import {
    IdCard,
    ChevronDown,
    Search,
    GraduationCap,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
  } from "lucide-vue-next";
  import AttendanceCard from "@/components/Attendence/ActionButtons.vue";
  import EmptyStateCard from "@/components/Attendence/EmptyState.vue";
  import AttendanceDetails from "@/components/Attendence/AttendanceDetails.vue";
  import ShowSuccess from "@/components/Attendence/Success.vue";
  import Banner from "@/components/Banner/StudentBanner.vue";
  // 🔁 Auto Refresh Interval
  const autoRefreshInterval = ref(null);
  // 🧠 Store & Local State
  const store = useStore();
  const { showToast } = useGlobalToast();
  const currentView = ref("attendance");
  const attendanceDate = ref("");
  const attendance = ref({});
  const remarks = ref({});
  const alreadyMarked = ref({});
  const showSuccessToast = ref(false);
  const selectedClass = ref(""); // Default to empty string for "All Classes"
  const search = ref("");
  const students = ref([]);
  const openDropdown = ref(null);
  const selectedDate = ref("");

  // 🔄 Loading States
  const isLoading = ref(false);
  const isContentLoading = ref(false);
  const isSearching = ref(false);

  const loadingMessage = ref("Loading attendance data...");

  // 📱 Mobile State
  const showMobileFilters = ref(false);

  // 📄 Pagination State
  const currentPage = ref(1);
  const limit = ref(20);
  const totalResults = computed(() => store.getters.getStudentPagination.total);
  const totalPages = computed(() => store.getters.getStudentPagination.pages);

  // Fetch classes from the store using a computed property
  const availableClasses = computed(() => store.getters.allClasses || []);

  // 🔘 Reactive state for individual saving states
  const savingStates = ref({});

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const toggleDropdown = (studentId) => {
    openDropdown.value = openDropdown.value === studentId ? null : studentId;
  };

  const closeDropdown = () => {
    openDropdown.value = null;
  };

  // Close dropdown when clicking outside
  const handleClickOutside = (event) => {
    if (!event.target.closest(".relative")) {
      closeDropdown();
    }
  };

  const addClickOutsideListener = () => {
    if (typeof window !== "undefined") {
      document.addEventListener("click", handleClickOutside);
    }
  };

  const removeClickOutsideListener = () => {
    if (typeof window !== "undefined") {
      document.removeEventListener("click", handleClickOutside);
    }
  };

  // ✅ Mounted: Fetch initial data
  onMounted(async () => {
    isLoading.value = true;
    loadingMessage.value = "Initializing attendance system...";
    try {
      const today = new Date();
      attendanceDate.value = today.toISOString().split("T")[0];
      selectedDate.value = getCurrentDate();
      await store.dispatch("fetchAllClasses"); // Fetch all classes

      // Load last selected class from localStorage if available
      const savedClass = localStorage.getItem("lastSelectedClass");
      if (savedClass) {
        selectedClass.value = savedClass;
      } else {
        selectedClass.value = ""; // default All Classes
      }

      await fetchFilteredStudents();
      startAutoRefresh();
      addClickOutsideListener();
    } finally {
      isLoading.value = false;
    }
  });

  // 🔄 Fetch Students (with pagination support)
  const fetchFilteredStudents = async (page = 1, silent = false) => {
    const isInitialLoad = page === 1 && students.value.length === 0;

    if (!silent) {
      if (isInitialLoad) {
        isLoading.value = true;
        loadingMessage.value = "Loading students...";
      } else {
        isContentLoading.value = true;
      }
    }

    try {
      currentPage.value = page;

      const dispatchParams = {
        class: selectedClass.value,
        search: search.value.trim(),
        page: currentPage.value,
        limit: limit.value,
      };

      if (selectedDate.value && selectedDate.value !== getCurrentDate()) {
        dispatchParams.date = selectedDate.value;
      }

      await store.dispatch("fetchStudents", dispatchParams);

      const newStudents = [...store.getters.allStudents].sort((a, b) => {
        const rollA = parseInt(a.rollNumber);
        const rollB = parseInt(b.rollNumber);
        return rollA - rollB;
      });

      // ✅ Update data without resetting UI harshly
      students.value = newStudents;

      // ✅ CLEAR OLD DATA FIRST
      attendance.value = {};
      alreadyMarked.value = {};
      remarks.value = {};

      // Update attendance states silently
      newStudents.forEach((student) => {
        if (student.attendanceStatus) {
          attendance.value[student._id] = student.attendanceStatus;
          alreadyMarked.value[student._id] = true;
        }
        if (student.remarks) {
          remarks.value[student._id] = student.remarks;
        }
      });
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      if (!silent) {
        isLoading.value = false;
        isContentLoading.value = false;
        isSearching.value = false;
      }
    }
  };

  // 🔁 Watch for Class or Search changes
  let searchTimeout;
  watch(search, () => {
    clearTimeout(searchTimeout);
    isSearching.value = true;
    searchTimeout = setTimeout(() => {
      fetchFilteredStudents(1);
    }, 300);
  });

  watch(selectedClass, () => {
    // Save selected class to localStorage
    localStorage.setItem("lastSelectedClass", selectedClass.value);

    isContentLoading.value = true;
    const className =
      availableClasses.value.find((cls) => cls._id === selectedClass.value)
        ?.name || "All Classes";
    loadingMessage.value = `Loading ${className} students...`;
    fetchFilteredStudents(1);
  });

  watch(selectedDate, () => {
    isContentLoading.value = true;
    loadingMessage.value = "Loading students for selected date...";
    fetchFilteredStudents(1);
  });

  // 🔄 Refresh Data
  const refreshData = async () => {
    isLoading.value = true;
    loadingMessage.value = "Refreshing data...";
    await fetchFilteredStudents(currentPage.value);
  };

  // 📚 Class name display helper
  const getClassName = (classId) => {
    const foundClass = availableClasses.value.find(
      (cls) => cls._id === classId,
    );
    return foundClass ? foundClass.name : "All Classes";
  };

  // 📊 Count helpers
  const getPresentCount = () =>
    Object.values(attendance.value).filter((v) => v === "present").length;
  const getAbsentCount = () =>
    Object.values(attendance.value).filter((v) => v === "absent").length;
  const getLateCount = () =>
    Object.values(attendance.value).filter((v) => v === "late").length;

  // ✅ Bulk Marking
  const markAllPresent = async () => {
    isLoading.value = true;
    loadingMessage.value = "Marking all students as present...";

    try {
      await store.dispatch("markAllPresent", {
        classId: selectedClass.value,
        date: selectedDate.value,
      });

      showSuccessToast.value = true;
      setTimeout(() => (showSuccessToast.value = false), 2000);

      fetchFilteredStudents(); // refresh UI
      fetchTodaysData();
    } finally {
      isLoading.value = false;
    }
  };
  const markAllAbsent = async () => {
    isLoading.value = true;
    loadingMessage.value = "Marking all students as absent...";

    try {
      await store.dispatch("markAllAbsent", {
        classId: selectedClass.value,
        date: selectedDate.value,
      });

      showSuccessToast.value = true;
      setTimeout(() => (showSuccessToast.value = false), 2000);

      fetchFilteredStudents();
      fetchTodaysData();
    } finally {
      isLoading.value = false;
    }
  };

  const undoAttendance = async (studentId) => {
    savingStates.value[studentId] = true;

    try {
      delete attendance.value[studentId];
      delete remarks.value[studentId];
      await store.dispatch("markAttendance", {
        studentId,
        status: "",
        subject: "",
        notes: "",
        date: selectedDate.value,
      });
      alreadyMarked.value[studentId] = false;
      showToast("success", "Attendance undone successfully.");
      fetchTodaysData();
    } catch (error) {
      showToast("error", "Failed to undo attendance. Please try again.");
    } finally {
      savingStates.value[studentId] = false;
    }
  };

  // 🔘 Set individual attendance and save immediately
  const setAttendance = async (studentId, status) => {
    savingStates.value[studentId] = true;

    try {
      attendance.value[studentId] = status;
      const notes = remarks.value[studentId] || "";
      await store.dispatch("markAttendance", {
        studentId,
        status,
        subject: "",
        notes,
        date: selectedDate.value,
      });
      alreadyMarked.value[studentId] = true;
      showSuccessToast.value = true;
      setTimeout(() => (showSuccessToast.value = false), 2000);
      fetchTodaysData();
    } catch (error) {
      console.error("Error while saving attendance:", error);
      delete attendance.value[studentId];
      alert("Failed to save attendance. Please try again.");
    } finally {
      savingStates.value[studentId] = false;
    }
  };

  const fetchTodaysData = () => {
    store.dispatch("fetchTodaysAttendancePercentage");
  };

  // 🔁 Pagination Button Click
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value && !isLoading.value) {
      isContentLoading.value = true;
      loadingMessage.value = `Loading page ${page}...`;
      fetchFilteredStudents(page);
    }
  };
  const startAutoRefresh = () => {
    autoRefreshInterval.value = setInterval(() => {
      if (!isLoading.value && !isContentLoading.value) {
        // 🔥 Silent background refresh
        fetchFilteredStudents(currentPage.value, true);
      }
    }, 5000); // 5 seconds
  };
  computed(() => Object.keys(attendance.value).length > 0);

  onUnmounted(() => {
    removeClickOutsideListener();
    if (autoRefreshInterval.value) {
      clearInterval(autoRefreshInterval.value);
    }
  });
</script>

<style scoped>
  @keyframes jump-in {
    0% {
      opacity: 0;
      transform: translateY(100px) scale(0.8);
    }
    50% {
      opacity: 1;
      transform: translateY(-20px) scale(1.05);
    }
    75% {
      transform: translateY(10px) scale(0.98);
    }
    100% {
      transform: translateY(0) scale(1);
    }
  }

  .animate-jump-in {
    animation: jump-in 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  * {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }

  input:focus,
  select:focus,
  button:focus {
    outline: none;
  }

  .overflow-x-auto::-webkit-scrollbar {
    height: 6px;
  }

  .overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  .backdrop-blur-sm {
    backdrop-filter: blur(4px);
  }

  .disabled\:opacity-50:disabled {
    opacity: 0.5;
  }

  .disabled\:cursor-not-allowed:disabled {
    cursor: not-allowed;
  }

  .disabled\:transform-none:disabled {
    transform: none;
  }

  @media (max-width: 640px) {
    button {
      min-height: 44px;
      min-width: 44px;
    }

    input,
    select {
      min-height: 44px;
    }

    .text-xs {
      font-size: 0.75rem;
      line-height: 1rem;
    }

    .text-sm {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }

    .container.mx-auto.p-4 {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    .max-w-7xl.mx-auto {
      width: 100%;
    }

    .block.md\:hidden.space-y-4 > div {
      padding: 1rem;
      border-radius: 0.75rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -2px rgba(0, 0, 0, 0.06);
      transition: all 0.2s ease-in-out;
    }
  }

  @media (min-width: 641px) and (max-width: 1023px) {
    .p-4 {
      padding: 1.5rem;
    }

    .gap-3 {
      gap: 0.75rem;
    }
  }

  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    .border {
      border-width: 0.5px;
    }

    .shadow-lg {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>
